import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Info, MapPinned, Package } from 'lucide-react';
import { getPillarBySlug } from '../data/productCatalog';
import { BROCHURE_PDF } from '../data/siteContent';

function parseApplicationSegments(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\s*\/\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Brochure-style: areas read as tags, not a vertical bullet list */
function ApplicationsDisplay({ text, variant = 'default' }) {
  const parts = parseApplicationSegments(text);
  const compact = variant === 'compact';
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return (
      <p className={`leading-relaxed text-neutral-700 ${compact ? 'text-[13px]' : 'text-[14px]'}`}>{parts[0]}</p>
    );
  }
  return (
    <ul className="flex flex-wrap gap-1.5 sm:gap-2" aria-label="Applications">
      {parts.map((a) => (
        <li
          key={a}
          className={[
            'inline-flex max-w-full rounded-full border border-emerald-200/80 bg-gradient-to-b from-emerald-50/90 to-white font-sans font-medium leading-snug text-neutral-800 shadow-sm',
            compact ? 'px-2 py-1 text-[11px] sm:text-xs' : 'px-3 py-1.5 text-[13px]',
          ].join(' ')}
        >
          {a}
        </li>
      ))}
    </ul>
  );
}

const ProductPillarPage = () => {
  const { pillarSlug } = useParams();
  const pillar = getPillarBySlug(pillarSlug);

  const systemAnchors = useMemo(() => {
    if (!pillar) return [];
    return pillar.systems.map((s) => ({ id: s.id, label: s.name }));
  }, [pillar]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pillarSlug]);

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [pillar]);

  if (!pillar) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-neutral-600 mb-6">Category not found.</p>
        <Link to="/products" className="text-brand-800 font-semibold hover:underline">
          Back to products
        </Link>
      </div>
    );
  }

  return (
    <div className="product-pillar-page min-h-screen bg-[#f4f6f9]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-[#0a3044] pb-20 pt-12 md:pt-16">
        <div className="absolute inset-0 industrial-grid opacity-[0.12]" />
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-8 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-brand-200/90">
            <Link to="/" className="transition-colors hover:text-white">
              Home
            </Link>
            <span className="text-brand-400/80">/</span>
            <Link to="/products" className="transition-colors hover:text-white">
              Products
            </Link>
            <span className="text-brand-400/80">/</span>
            <span className="text-white">{pillar.title}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-200">
            <Package className="h-3.5 w-3.5" strokeWidth={2.5} />
            Category
          </div>
          <h1 className="font-heading mt-4 max-w-4xl text-3xl font-extrabold leading-tight tracking-tight text-white md:text-5xl md:leading-[1.1]">
            {pillar.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-brand-100/90 md:text-lg">
            Product lines with description and applications as per technical brochure.
          </p>
        </div>
      </section>

      {/* Overview cards */}
      <section className="relative z-20 -mt-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-neutral-200/80 bg-white p-6 shadow-[0_4px_24px_rgba(13,40,71,0.08)] md:p-7">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                <Info className="h-5 w-5" strokeWidth={2} />
              </span>
              <h2 className="font-heading text-lg font-bold text-neutral-900">What it is</h2>
            </div>
            <p className="text-[15px] leading-relaxed text-neutral-600">{pillar.overview.what}</p>
          </div>
          <div className="rounded-2xl border border-neutral-200/80 bg-white p-6 shadow-[0_4px_24px_rgba(13,40,71,0.08)] md:p-7">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                <MapPinned className="h-5 w-5" strokeWidth={2} />
              </span>
              <h2 className="font-heading text-lg font-bold text-neutral-900">Where it is used</h2>
            </div>
            <p className="text-[15px] leading-relaxed text-neutral-600">{pillar.overview.whereUsed}</p>
          </div>
        </div>
      </section>

      {/* Systems — full content width (no side column); nav is horizontal so the table spans the full max-w-5xl column */}
      <section className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 md:py-12 lg:px-8">
        <div className="mb-6 text-center md:mb-8 md:text-left">
          <h2 className="font-heading text-2xl font-bold text-neutral-900 md:text-3xl">Product systems</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-neutral-500 md:mx-0">
            Beck Bond / SV Bond lines with description and typical application areas — aligned to the technical brochure layout.
          </p>
        </div>

        <nav
          className="mb-4 flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible"
          aria-label="Jump to a system"
        >
          {systemAnchors.map((a, i) => (
            <a
              key={a.id}
              href={`#${a.id}`}
              className="shrink-0 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-left text-xs font-medium leading-snug text-neutral-700 shadow-sm transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-900"
            >
              <span className="font-semibold text-brand-700">{i + 1}.</span>{' '}
              <span className="line-clamp-2 inline-block max-w-[220px] align-top sm:max-w-none">{a.label}</span>
            </a>
          ))}
        </nav>

        <p className="mb-3 text-center text-xs text-neutral-500 md:text-left">
          On small screens, swipe the table sideways to see all columns.
        </p>

        <div className="w-full min-w-0 overflow-x-auto rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_40px_rgba(13,40,71,0.06)]">
          <table className="w-full min-w-[720px] border-collapse text-left font-sans text-[15px] antialiased">
                <thead>
                  <tr className="bg-gradient-to-r from-[#0a3d32] via-[#0f5c4a] to-[#0a3d32] text-white">
                    <th className="sticky left-0 z-[2] w-12 border-b border-white/10 bg-[#0a3d32] px-2 py-3.5 text-center font-sans text-xs font-bold uppercase tracking-wider shadow-[2px_0_8px_rgba(0,0,0,0.06)]">
                      #
                    </th>
                    <th className="min-w-[200px] border-b border-white/10 px-3 py-3.5 font-sans text-xs font-bold uppercase tracking-wider">
                      System
                    </th>
                    <th className="min-w-[180px] border-b border-white/10 px-3 py-3.5 font-sans text-xs font-bold uppercase tracking-wider">
                      Description
                    </th>
                    <th className="min-w-[240px] border-b border-white/10 px-3 py-3.5 font-sans text-xs font-bold uppercase tracking-wider">
                      Applications
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pillar.systems.map((sys, index) => {
                    const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-[#f7faf8]';
                    return (
                    <tr
                      key={sys.id}
                      id={sys.id}
                      className={`scroll-mt-28 border-b border-neutral-200/90 ${rowBg}`}
                    >
                      <td
                        className={`sticky left-0 z-[1] border-r border-neutral-200/80 px-2 py-4 text-center align-top font-sans text-sm font-bold text-brand-800 shadow-[2px_0_8px_rgba(0,0,0,0.04)] ${rowBg}`}
                      >
                        {index + 1}
                      </td>
                      <td className="px-3 py-4 align-top font-sans text-sm font-semibold leading-snug text-neutral-900">{sys.name}</td>
                      <td className="px-3 py-4 align-top font-sans text-sm font-normal not-italic leading-relaxed text-neutral-700">
                        {sys.description}
                      </td>
                      <td className="px-3 py-4 align-top font-sans text-sm">
                        <ApplicationsDisplay text={sys.applications} variant="compact" />
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
          </table>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-neutral-200/80 bg-white py-12">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-800 transition-colors hover:text-brand-600"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            All product categories
          </Link>
          <a
            href={BROCHURE_PDF}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-small bg-brand-900 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-brand-900/25 transition hover:bg-brand-800"
          >
            <Download className="h-4 w-4" />
            Download brochure (PDF)
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductPillarPage;
